import React, { useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Import Tournament from Link Component for Clubs
 * Imports tournaments from external platforms (e.g., CrearLiga)
 * Creates new soci for participants not found in the club
 */
const ImportTorneoFromLink = ({ token, onSuccess, onCancel }) => {
  const [step, setStep] = useState('input'); // input, analyzing, preview, importing, success
  const [url, setUrl] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [tournamentName, setTournamentName] = useState('');
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [showFormatSelector, setShowFormatSelector] = useState(false);
  const [error, setError] = useState(null);
  const [tournamentStartDate, setTournamentStartDate] = useState('');

  // Format labels for display
  const FORMAT_LABELS = {
    'campionato': { emoji: '📊', label: 'Campionato', desc: 'Classifica con punti e giornate' },
    'coppa': { emoji: '🏆', label: 'Coppa', desc: 'Eliminazione diretta (tabellone)' },
    'gironi_eliminazione': { emoji: '🎯', label: 'Gironi + Eliminazione', desc: 'Fase a gironi seguita da tabellone' },
    'gironi': { emoji: '🔄', label: 'Solo Gironi', desc: 'Fase a gironi senza eliminazione' },
    'multi_serata': { emoji: '🌙', label: 'Multi-Serata', desc: 'Torneo diviso in più serate' },
    'doppia_eliminazione': { emoji: '🔀', label: 'Doppia Eliminazione', desc: 'Tabellone principale + ripescati' }
  };

  // API helper
  const api = useCallback(async (endpoint, options = {}) => {
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers
    };
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers
    });
    
    return response;
  }, [token]);

  // Analyze URL
  const handleAnalyze = async () => {
    if (!url.trim()) {
      setError('Inserisci un link valido');
      return;
    }

    setError(null);
    setStep('analyzing');

    try {
      const response = await api('/api/clubs/import-torneo-link/analyze', {
        method: 'POST',
        body: JSON.stringify({ url: url.trim() })
      });

      const data = await response.json();

      if (data.success) {
        setAnalysisResult(data);
        setTournamentName(data.tournament_name || '');
        // Set the tournament start date if extracted by the parser
        if (data.tournament_start_date) {
          setTournamentStartDate(data.tournament_start_date);
        } else {
          setTournamentStartDate('');
        }
        // Pre-select the detected format if confidence is high
        if (data.format_confidence === 'high' && data.detected_format !== 'unknown') {
          setSelectedFormat(data.detected_format);
          setShowFormatSelector(false);
        } else {
          setSelectedFormat(null); // User must choose
          setShowFormatSelector(true);
        }
        setStep('preview');
      } else {
        setError(data.error || 'Errore durante l\'analisi');
        setStep('input');
      }
    } catch (err) {
      console.error('Analysis error:', err);
      setError('Errore di connessione');
      setStep('input');
    }
  };

  // Create tournament
  const handleCreate = async () => {
    if (!tournamentName.trim()) {
      setError('Inserisci un nome per il torneo');
      return;
    }

    // Require tournament start date if not auto-detected
    if (!tournamentStartDate) {
      setError('Inserisci la data di inizio del torneo originale');
      return;
    }

    // Check if format selection is required
    const requiresSelection = analysisResult?.requires_format_selection || 
                             analysisResult?.format_confidence === 'low' ||
                             analysisResult?.detected_format === 'unknown';
    
    if (requiresSelection && !selectedFormat) {
      setError('Seleziona il formato del torneo');
      return;
    }

    const finalFormat = selectedFormat || analysisResult?.detected_format || 'campionato';

    setStep('importing');
    setError(null);

    try {
      const response = await api('/api/clubs/import-torneo-link/create', {
        method: 'POST',
        body: JSON.stringify({
          tournament_name: tournamentName,
          source_url: url.trim(),
          participants: analysisResult.participants,
          matches: analysisResult.matches,
          is_completed: analysisResult.is_completed,
          detected_format: finalFormat,
          // Pass structured data
          groups: analysisResult.groups || [],
          knockout_bracket: analysisResult.knockout_bracket || {},
          player_stats: analysisResult.player_stats || {},
          winner: analysisResult.extra_info?.winner || null,
          finalist: analysisResult.extra_info?.finalist || null,
          tournament_start_date: tournamentStartDate || null
        })
      });

      const data = await response.json();

      if (data.success) {
        setAnalysisResult(prev => ({
          ...prev,
          createResult: data
        }));
        setStep('success');
        if (onSuccess) {
          setTimeout(() => onSuccess(data.tournament_id), 2000);
        }
      } else {
        setError(data.detail || data.error || 'Errore durante la creazione');
        setStep('preview');
      }
    } catch (err) {
      console.error('Create error:', err);
      setError('Errore di connessione');
      setStep('preview');
    }
  };

  // Reset
  const handleReset = () => {
    setStep('input');
    setUrl('');
    setAnalysisResult(null);
    setTournamentName('');
    setSelectedFormat(null);
    setShowFormatSelector(false);
    setError(null);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <span className="text-2xl">🔗</span> Importa Torneo da Link
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Importa tornei da piattaforme esterne con tutti i partecipanti e risultati
          </p>
        </div>
        {onCancel && step !== 'success' && (
          <button 
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 p-2"
          >
            ✕
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Step: Input */}
      {step === 'input' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Link Torneo
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://oscmagnagati.leaguerepublic.com/... o https://subbuteo.crearliga.com/..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="import-link-input"
            />
            <p className="text-xs text-gray-400 mt-1">
              Incolla il link completo della pagina del torneo
            </p>
          </div>
          
          {/* Supported Sites Info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs font-medium text-gray-600 mb-2">🌐 Siti supportati:</p>
            <div className="flex flex-wrap gap-2">
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">leaguerepublic.com</span>
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">crearliga.com</span>
            </div>
          </div>
          
          <button
            onClick={handleAnalyze}
            disabled={!url.trim()}
            className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            data-testid="analyze-link-btn"
          >
            🔍 Analizza Torneo
          </button>
        </div>
      )}

      {/* Step: Analyzing */}
      {step === 'analyzing' && (
        <div className="text-center py-12">
          <div className="relative inline-block">
            <div className="animate-spin h-16 w-16 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl">🔗</span>
            </div>
          </div>
          <p className="text-gray-600 mt-4 text-lg">Analisi in corso...</p>
          <p className="text-gray-400 text-sm mt-2">Estrazione dati del torneo</p>
        </div>
      )}

      {/* Step: Preview */}
      {step === 'preview' && analysisResult && (
        <div className="space-y-5">
          {/* Source Badge */}
          {analysisResult.parser_used && (
            <div className="flex items-center gap-2 text-sm">
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                {analysisResult.parser_used === 'leaguerepublic' ? '🌐 LeagueRepublic' : 
                 analysisResult.parser_used === 'crearliga' ? '🏆 CrearLiga' : 
                 analysisResult.parser_used}
              </span>
              <span className="text-gray-400">riconosciuto automaticamente</span>
            </div>
          )}
          
          {/* Format Detection Section */}
          {(() => {
            const requiresSelection = analysisResult.requires_format_selection || 
                                     analysisResult.format_confidence === 'low' ||
                                     analysisResult.detected_format === 'unknown' ||
                                     showFormatSelector;
            const formatInfo = FORMAT_LABELS[selectedFormat || analysisResult.detected_format] || {};
            
            if (requiresSelection) {
              // Show format selection UI
              return (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-2 mb-3">
                    <span className="text-amber-500 text-lg">⚠️</span>
                    <div>
                      <p className="text-amber-800 font-medium">Seleziona il formato del torneo</p>
                      <p className="text-amber-600 text-sm">
                        {analysisResult.format_confidence === 'low' || analysisResult.detected_format === 'unknown'
                          ? 'Non è stato possibile determinare automaticamente il formato. Scegli tu:'
                          : 'Seleziona il formato corretto per questo torneo:'}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {Object.keys(FORMAT_LABELS).map((format) => {
                      const info = FORMAT_LABELS[format];
                      if (!info) return null;
                      const isSelected = selectedFormat === format;
                      const isRecommended = format === analysisResult.detected_format && analysisResult.format_confidence !== 'low';
                      return (
                        <button
                          key={format}
                          onClick={() => {
                            setSelectedFormat(format);
                            setShowFormatSelector(false);
                          }}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            isSelected 
                              ? 'border-blue-500 bg-blue-50' 
                              : isRecommended
                                ? 'border-green-300 bg-green-50 hover:border-green-400'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                          data-testid={`format-option-${format}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{info.emoji}</span>
                            <span className={`font-medium ${isSelected ? 'text-blue-700' : 'text-gray-800'}`}>
                              {info.label}
                            </span>
                            {isRecommended && !isSelected && (
                              <span className="ml-auto text-xs px-1.5 py-0.5 bg-green-200 text-green-700 rounded">Consigliato</span>
                            )}
                            {isSelected && <span className="ml-auto text-blue-500">✓</span>}
                          </div>
                          <p className="text-xs text-gray-500 mt-1 ml-7">{info.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            } else {
              // Show detected format badge (high confidence)
              return (
                <div className="flex items-center gap-2 text-sm">
                  <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
                    <span>{formatInfo.emoji || '📋'}</span>
                    <span>{formatInfo.label || selectedFormat || analysisResult.detected_format}</span>
                    <span className="text-green-500 ml-1">✓</span>
                  </span>
                  <span className="text-gray-400">formato rilevato</span>
                  {/* Allow changing even if auto-detected */}
                  <button 
                    onClick={() => setShowFormatSelector(true)}
                    className="text-xs text-blue-500 hover:underline ml-2"
                  >
                    Cambia
                  </button>
                </div>
              );
            }
          })()}
          
          {/* Tournament Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome Torneo *
            </label>
            <input
              type="text"
              value={tournamentName}
              onChange={(e) => setTournamentName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="tournament-name-input"
            />
          </div>

          {/* Tournament Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data inizio torneo originale *
            </label>
            {analysisResult.tournament_start_date ? (
              <div className="flex items-center gap-3">
                <input
                  type="date"
                  value={tournamentStartDate}
                  onChange={(e) => setTournamentStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="tournament-date-input"
                />
                <span className="text-xs text-green-600 whitespace-nowrap flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                  Rilevata automaticamente
                </span>
              </div>
            ) : (
              <div>
                <div className="flex items-start gap-2 mb-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                  <p className="text-sm text-amber-700">
                    Non riusciamo a leggere la data originale dal sito. Inseriscila manualmente.
                  </p>
                </div>
                <input
                  type="date"
                  value={tournamentStartDate}
                  onChange={(e) => setTournamentStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 bg-amber-50"
                  data-testid="tournament-date-input"
                />
              </div>
            )}
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-blue-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">
                {analysisResult.statistics?.total_participants || 0}
              </div>
              <div className="text-xs text-blue-700">Partecipanti</div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">
                {analysisResult.statistics?.played_matches || 0}
              </div>
              <div className="text-xs text-green-700">Partite Giocate</div>
            </div>
            <div className="bg-yellow-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {analysisResult.statistics?.pending_matches || 0}
              </div>
              <div className="text-xs text-yellow-700">Da Giocare</div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-600">
                {analysisResult.statistics?.total_goals || 0}
              </div>
              <div className="text-xs text-purple-700">Gol Totali</div>
            </div>
          </div>

          {/* Matching Summary */}
          {analysisResult.matching_summary && (
            <div className="bg-gray-50 p-4 rounded-lg" data-testid="matching-summary">
              <h4 className="font-medium text-gray-800 mb-2">👥 Riepilogo Anagrafica</h4>
              <div className="flex gap-4 text-sm">
                <span className="text-green-600">
                  ✓ {analysisResult.matching_summary.matched} abbinati a soci esistenti
                </span>
                <span className="text-blue-600">
                  + {analysisResult.matching_summary.new} nuovi da creare
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                I nuovi partecipanti verranno aggiunti automaticamente all'anagrafica del club
              </p>
            </div>
          )}

          {/* Participants List */}
          <div className="bg-gray-50 p-4 rounded-lg max-h-60 overflow-y-auto">
            <h4 className="font-medium text-gray-800 mb-3">📋 Partecipanti</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {analysisResult.participants?.map((p, i) => (
                <div 
                  key={i}
                  className={`flex items-center gap-2 p-2 rounded ${
                    p.tipo === 'esistente' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  <span className="text-lg">
                    {p.tipo === 'esistente' ? '✓' : '➕'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{p.nome}</div>
                    {p.squadra && (
                      <div className="text-xs opacity-75 truncate">{p.squadra}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sample Matches */}
          {analysisResult.matches?.length > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-3">
                ⚽ Partite ({analysisResult.matches.length} totali)
              </h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {analysisResult.matches.slice(0, 5).map((m, i) => (
                  <div key={i} className="flex items-center justify-between bg-white p-2 rounded text-sm">
                    <span className="flex-1 text-right truncate pr-2">{m.giocatore_casa}</span>
                    <span className="font-bold px-3 bg-gray-100 rounded">
                      {m.gol_casa} - {m.gol_trasferta}
                    </span>
                    <span className="flex-1 truncate pl-2">{m.giocatore_trasferta}</span>
                  </div>
                ))}
                {analysisResult.matches.length > 5 && (
                  <p className="text-center text-gray-500 text-xs">
                    ... e altre {analysisResult.matches.length - 5} partite
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Status */}
          <div className={`p-3 rounded-lg text-center ${
            analysisResult.is_completed 
              ? 'bg-green-100 text-green-800' 
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {analysisResult.is_completed 
              ? '✅ Torneo completato - Verrà importato come terminato'
              : '🔄 Torneo in corso - Verrà importato come attivo'
            }
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleCreate}
              disabled={!tournamentName.trim()}
              className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              ✅ Importa Torneo
            </button>
            <button
              onClick={handleReset}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              ❌
            </button>
          </div>
        </div>
      )}

      {/* Step: Importing */}
      {step === 'importing' && (
        <div className="text-center py-12">
          <div className="animate-spin h-16 w-16 border-4 border-green-200 border-t-green-600 rounded-full mx-auto"></div>
          <p className="text-gray-600 mt-4 text-lg">Creazione torneo in corso...</p>
          <p className="text-gray-400 text-sm mt-2">
            Creazione soci e salvataggio partite
          </p>
        </div>
      )}

      {/* Step: Success */}
      {step === 'success' && (
        <div className="text-center py-8">
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-2xl font-bold text-green-600 mb-2">
            Torneo Importato!
          </h3>
          <p className="text-gray-600 mb-4">
            "{tournamentName}" è stato creato con successo
          </p>
          
          {analysisResult?.createResult?.statistics && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4 text-left">
              <h4 className="font-medium mb-2">📊 Riepilogo:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>
                  📋 Formato: <span className="font-medium">
                    {FORMAT_LABELS[selectedFormat || analysisResult?.detected_format]?.label || 'Torneo'}
                  </span>
                </li>
                <li>👥 {analysisResult.createResult.statistics.participants} partecipanti</li>
                <li>⚽ {analysisResult.createResult.statistics.matches} partite</li>
                {analysisResult.createResult.statistics.new_soci_created > 0 && (
                  <li className="text-blue-600">
                    ➕ {analysisResult.createResult.statistics.new_soci_created} nuovi soci creati
                  </li>
                )}
              </ul>
            </div>
          )}
          
          <button
            onClick={handleReset}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
          >
            🔗 Importa un altro torneo
          </button>
        </div>
      )}
    </div>
  );
};

export default ImportTorneoFromLink;
